// pages/ProjectDetail.jsx - COMPLETE WITH INLINE EDITING
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  MoreVertical,
  FileText,
  Paperclip,
  Activity,
  Plus,
  Briefcase,
  CheckSquare,
  Wifi,
  PhoneCall,
  ExternalLink,
  Building2,
  User as UserIcon,
  Calendar,
  AlertCircle,
  Clock,
  Save,
  X as CloseIcon,
  Loader,
  Search,
  Info,
} from "lucide-react";
import apiService from "../../services/api";
import CreateBroadbandInfoModal from "../../components/CreateBroadbandInfoModal";
import CreatePortsOrderModal from "../../components/CreatePortsOrderModal";
import CreateTaskModal from "../../components/CreateTaskModal";
import { useTheme } from "../../contexts/ThemeContext";

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Modal states
  const [showCreateBroadband, setShowCreateBroadband] = useState(false);
  const [showCreatePorts, setShowCreatePorts] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);

  // Related data
  const [activity, setActivity] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [broadbandInfos, setBroadbandInfos] = useState([]);
  const [portsOrders, setPortsOrders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);

  // Note creation
  const [newNote, setNewNote] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);

  // Inline editing
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingField, setSavingField] = useState(false);

  // Search states for lookups
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [showContactSearch, setShowContactSearch] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [files, setFiles] = useState([]);

  // Drag and drop states
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [filesToUpload, setFilesToUpload] = useState(null);
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);

  // Add fetch function:
  const fetchFiles = async () => {
    try {
      const response = await apiService.getProjectFiles(id);
      setFiles(response.data || []);
    } catch (error) {
      console.error("Error fetching files:", error);
    }
  };
  useEffect(() => {
    fetchProject();
    fetchActivity();
    fetchNotes();
    fetchFiles();
    fetchAttachments();
    fetchUsers();
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProject(id);
      setProject(response.data);

      if (response.data.projectTasks) setTasks(response.data.projectTasks);
      if (response.data.broadbandInformations)
        setBroadbandInfos(response.data.broadbandInformations);
      if (response.data.portsOrders) setPortsOrders(response.data.portsOrders);
    } catch (error) {
      console.error("Error fetching project:", error);
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchActivity = async () => {
    try {
      const response = await apiService.getProjectActivity(id);
      setActivity(response.data);
    } catch (error) {
      console.error("Error fetching activity:", error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await apiService.getProjectNotes(id);
      setNotes(response.data);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await apiService.getProjectAttachments(id);
      setAttachments(response.data);
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this project? This action cannot be undone.",
      )
    ) {
      try {
        await apiService.deleteProject(id);
        navigate("/projects");
      } catch (error) {
        console.error("Error deleting project:", error);
        if (error.response?.data?.relatedRecords) {
          alert(
            `Cannot delete project. It has:\n` +
              `${error.response.data.relatedRecords.tasks} tasks\n` +
              `${error.response.data.relatedRecords.broadbandInfos} broadband records\n` +
              `${error.response.data.relatedRecords.portsOrders} ports orders`,
          );
        } else {
          alert("Failed to delete project");
        }
      }
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.trim()) return;

    try {
      await apiService.createProjectNote(id, {
        title: newNoteTitle,
        body: newNote,
        isPrivate: isPrivateNote,
      });
      setNewNote("");
      setNewNoteTitle("");
      setIsPrivateNote(false);
      fetchNotes();
      fetchActivity();
    } catch (error) {
      console.error("Error creating note:", error);
      alert("Failed to create note");
    }
  };

  // Inline editing functions
  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditingValue(currentValue || "");

    // For lookup fields, set the search value
    if (field === "projectManagerId" && project.projectManager) {
      setUserSearch(project.projectManager.name);
    } else if (field === "surveyCompletedBy" && project.surveyCompleter) {
      setUserSearch(project.surveyCompleter.name);
    } else if (
      field === "clientSurveyContactId" &&
      project.clientSurveyContact
    ) {
      setContactSearch(
        `${project.clientSurveyContact.firstName} ${project.clientSurveyContact.lastName}`,
      );
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingValue("");
    setUserSearch("");
    setContactSearch("");
    setShowUserSearch(false);
    setShowContactSearch(false);
  };

  const saveFieldEdit = async (field) => {
    try {
      setSavingField(true);
      await apiService.updateProject(id, { [field]: editingValue });
      await fetchProject();
      setEditingField(null);
      setEditingValue("");
      setUserSearch("");
      setContactSearch("");
      setShowUserSearch(false);
      setShowContactSearch(false);
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setSavingField(false);
    }
  };

  // Handle checklist item toggle
  const handleChecklistToggle = async (field, currentValue) => {
    try {
      await apiService.updateProject(id, { [field]: !currentValue });
      await fetchProject();
    } catch (error) {
      console.error("Error updating checklist:", error);
      alert("Failed to update checklist item");
    }
  };

  // Fetch all company users
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await apiService.getCompanyUsers();
      const allUsers = response.data || response.users || [];
      setUsers(allUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Filter users based on search query
  const filterUsers = (query) => {
    if (!query || query.length < 2) {
      return [];
    }
    const lowercaseQuery = query.toLowerCase();
    return users.filter(
      (user) =>
        user.name?.toLowerCase().includes(lowercaseQuery) ||
        user.email?.toLowerCase().includes(lowercaseQuery),
    );
  };

  const searchContacts = async (query) => {
    if (!query || query.length < 2) {
      setContacts([]);
      return;
    }

    try {
      setLoadingContacts(true);
      const response = await apiService.getContacts({
        search: query,
        accountId: project.accountId,
        limit: 10,
      });
      setContacts(response.data || []);
    } catch (error) {
      console.error("Error searching contacts:", error);
    } finally {
      setLoadingContacts(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (contactSearch && editingField === "clientSurveyContactId") {
        searchContacts(contactSearch);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [contactSearch, editingField]);

  const selectUser = (user) => {
    setEditingValue(user.id);
    setUserSearch(user.name);
    setShowUserSearch(false);
  };

  const selectContact = (contact) => {
    setEditingValue(contact.id);
    setContactSearch(`${contact.firstName} ${contact.lastName}`);
    setShowContactSearch(false);
  };
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await apiService.uploadProjectFile(id, formData);
      fetchFiles();
      event.target.value = ""; // Reset input
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file");
    }
  };

  // Add delete handler:
  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    try {
      await apiService.deleteProjectFile(id, fileId);
      fetchFiles();
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Failed to delete file");
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setFilesToUpload(files);
      setShowUploadConfirmation(true);
    }
  };

  const confirmUpload = async () => {
    if (!filesToUpload) return;

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const formData = new FormData();
        formData.append("file", file);
        await apiService.uploadProjectFile(id, formData);
      }
      fetchFiles();
      setShowUploadConfirmation(false);
      setFilesToUpload(null);
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload files");
    }
  };

  const cancelUpload = () => {
    setShowUploadConfirmation(false);
    setFilesToUpload(null);
  };

  // Helper functions (add these):

  const getInitials = (name) => {
    return name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "PR";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatActivityDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    return d.toLocaleDateString();
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "broadband":
        return <Wifi className="text-green-500" size={16} />;
      case "ports_order":
        return <PhoneCall className="text-blue-500" size={16} />;
      case "project_task":
        return <CheckSquare className="text-purple-500" size={16} />;
      case "note":
        return <FileText className="text-yellow-500" size={16} />;
      default:
        return <Activity className="text-gray-500" size={16} />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      "Survey Requested":
        "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      "Awaiting Survey":
        "bg-orange-500/20 text-orange-400 border-orange-500/30",
      "Survey Booked": "bg-blue-500/20 text-blue-400 border-blue-500/30",
      "Survey Completed": "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
      Quotation: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      "Awaiting Finance Approval":
        "bg-pink-500/20 text-pink-400 border-pink-500/30",
      Porting: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
      "Broadband Install": "bg-teal-500/20 text-teal-400 border-teal-500/30",
      Complete: "bg-green-500/20 text-green-400 border-green-500/30",
      Cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    };
    return colors[status] || colors["Survey Requested"];
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"} flex items-center justify-center`}
      >
        <Loader
          className={`${isDarkMode ? "text-white" : "text-gray-900"} animate-spin`}
          size={32}
        />
      </div>
    );
  }

  if (!project) {
    return (
      <div
        className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"} flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Project not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div
        className={`border-b transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <button
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ArrowLeft size={20} />
              <span>Go Back</span>
            </button>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* Quick Actions */}

              <button
                onClick={() => setShowCreateBroadband(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <Wifi size={16} />
                <span className="hidden sm:inline">New Broadband</span>
              </button>

              <button
                onClick={() => setShowCreatePorts(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                <PhoneCall size={16} />
                <span className="hidden sm:inline">New Ports Order</span>
              </button>

              {/* Actions Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                >
                  <MoreVertical size={20} />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 border rounded-lg shadow-xl z-50 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
                    >
                      <FileText size={16} />
                      Copy Link
                    </button>
                    <div
                      className={`border-t my-1 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    ></div>
                    <button
                      onClick={() => {
                        handleDelete();
                        setShowActionsMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left flex items-center gap-2 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Project Header Info */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                  {getInitials(project.projectName)}
                </div>
                <div>
                  <h1
                    className={`text-xl sm:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {project.projectName}
                  </h1>
                  {project.projectNumber && (
                    <div
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      {project.projectNumber}
                    </div>
                  )}
                </div>

                {project.status && (
                  <span
                    className={`px-3 py-1 rounded-lg border text-sm font-medium ${getStatusColor(
                      project.status,
                    )}`}
                  >
                    {project.status}
                  </span>
                )}

                {project.priority && (
                  <span
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${
                      project.priority === "Urgent"
                        ? "text-red-500 bg-red-500/10"
                        : project.priority === "High"
                          ? "text-orange-500 bg-orange-500/10"
                          : project.priority === "Medium"
                            ? "text-yellow-500 bg-yellow-500/10"
                            : "text-blue-500 bg-blue-500/10"
                    }`}
                  >
                    {project.priority}
                  </span>
                )}

                {project.projectBlocked && (
                  <span className="px-3 py-1 rounded-lg text-sm font-medium text-red-500 bg-red-500/10 flex items-center gap-1">
                    <AlertCircle size={14} />
                    Blocked
                  </span>
                )}
              </div>

              <div
                className={`flex flex-wrap items-center gap-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {project.account && (
                  <button
                    onClick={() => navigate(`/accounts/${project.account.id}`)}
                    className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-blue-400" : "hover:text-blue-600"}`}
                  >
                    <Building2 size={16} />
                    <span>{project.account.name}</span>
                  </button>
                )}
                {project.startDate && (
                  <div className="flex items-center gap-2">
                    <Calendar size={16} />
                    <span>Started: {formatDate(project.startDate)}</span>
                  </div>
                )}
                {project.daysOpen !== null && (
                  <div className="flex items-center gap-2">
                    <Clock size={16} />
                    <span>{project.daysOpen} days open</span>
                  </div>
                )}
              </div>

              {project.projectBlockedReason && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="text-red-400 mt-0.5" size={16} />
                    <div>
                      <div className="text-sm font-medium text-red-400">
                        Project Blocked
                      </div>
                      <div
                        className={`text-sm mt-1 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                      >
                        {project.projectBlockedReason}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {project.owner && (
              <div className="flex flex-row sm:flex-col gap-3 flex-wrap">
                <div
                  className={`flex items-center gap-3 rounded-lg p-3 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-700" : "bg-gray-300"}`}
                  >
                    {project.owner.profileImageUrl ? (
                      <img
                        src={project.owner.profileImageUrl}
                        alt={project.owner.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span
                        className={`text-lg font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
                      >
                        {project.owner.name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <div
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Project Owner
                    </div>
                    <div
                      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {project.owner.name}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`rounded-lg border ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex border-b overflow-x-auto scrollbar-hide ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                {[
                  { id: "details", label: "Details", icon: Info },

                  {
                    id: "tasks",
                    label: `Tasks (${tasks.length})`,
                    icon: CheckSquare,
                  },
                  {
                    id: "broadband",
                    label: `Broadband (${broadbandInfos.length})`,
                    icon: Wifi,
                  },
                  {
                    id: "ports",
                    label: `Ports (${portsOrders.length})`,
                    icon: PhoneCall,
                  },
                  {
                    id: "notes",
                    label: `Notes (${notes.length})`,
                    icon: FileText,
                  },
                  {
                    id: "files",
                    label: `Files (${attachments.length})`,
                    icon: Paperclip,
                  },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.id
                        ? "text-blue-400 border-b-2 border-blue-400"
                        : isDarkMode
                          ? "text-gray-400 hover:text-white"
                          : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <tab.icon size={16} />
                      {tab.label}
                    </div>
                  </button>
                ))}
              </div>

              <div className="p-4 sm:p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <DetailsTab
                    project={project}
                    editingField={editingField}
                    editingValue={editingValue}
                    savingField={savingField}
                    startEditing={startEditing}
                    cancelEditing={cancelEditing}
                    saveFieldEdit={saveFieldEdit}
                    setEditingValue={setEditingValue}
                    userSearch={userSearch}
                    setUserSearch={setUserSearch}
                    contactSearch={contactSearch}
                    setContactSearch={setContactSearch}
                    showUserSearch={showUserSearch}
                    setShowUserSearch={setShowUserSearch}
                    showContactSearch={showContactSearch}
                    setShowContactSearch={setShowContactSearch}
                    users={users}
                    contacts={contacts}
                    loadingUsers={loadingUsers}
                    loadingContacts={loadingContacts}
                    selectUser={selectUser}
                    selectContact={selectContact}
                    filterUsers={filterUsers}
                    isDarkMode={isDarkMode}
                  />
                )}

                {/* Activity Tab */}
                {/* {activeTab === "activity" && (
                  <ActivityTab
                    activity={activity}
                    formatActivityDate={formatActivityDate}
                    getActivityIcon={getActivityIcon}
                  />
                )} */}

                {/* Tasks Tab */}
                {activeTab === "tasks" && (
                  <TasksTab
                    tasks={tasks}
                    onCreateNew={() => setShowCreateTask(true)}
                    navigate={navigate}
                    isDarkMode={isDarkMode}
                  />
                )}

                {/* Broadband Tab */}
                {activeTab === "broadband" && (
                  <BroadbandTab
                    broadbandInfos={broadbandInfos}
                    onCreateNew={() => setShowCreateBroadband(true)}
                    navigate={navigate}
                    isDarkMode={isDarkMode}
                  />
                )}

                {/* Ports Tab */}
                {activeTab === "ports" && (
                  <PortsTab
                    portsOrders={portsOrders}
                    onCreateNew={() => setShowCreatePorts(true)}
                    navigate={navigate}
                    isDarkMode={isDarkMode}
                  />
                )}

                {/* Notes Tab */}
                {activeTab === "notes" && (
                  <NotesTab notes={notes} isDarkMode={isDarkMode} />
                )}

                {/* Files Tab */}
                {activeTab === "files" && (
                  <FilesTab
                    files={files}
                    onUpload={handleFileUpload}
                    onDelete={handleDeleteFile}
                    isDarkMode={isDarkMode}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Project Details Card */}
            {/* <div className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}>
              <h3 className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}>Quick Information</h3>
              <div className="space-y-3">
                <DetailRow label="Type" value={project.type || "—"} isDarkMode={isDarkMode} />
                <DetailRow
                  label="Start Date"
                  value={formatDate(project.startDate)}
                  isDarkMode={isDarkMode}
                />
                <DetailRow
                  label="End Date"
                  value={formatDate(project.endDate)}
                  isDarkMode={isDarkMode}
                />
                <DetailRow
                  label="Days Open"
                  value={
                    project.daysOpen !== null ? `${project.daysOpen} days` : "—"
                  }
                  isDarkMode={isDarkMode}
                />
                <DetailRow
                  label="Installation Date"
                  value={formatDate(project.installationDate)}
                  isDarkMode={isDarkMode}
                />
                {project.projectManager && (
                  <DetailRow
                    label="Project Manager"
                    value={project.projectManager.name}
                    isDarkMode={isDarkMode}
                  />
                )}
                {project.clientSurveyContact && (
                  <DetailRow
                    label="Survey Contact"
                    value={`${project.clientSurveyContact.firstName} ${project.clientSurveyContact.lastName}`}
                    isDarkMode={isDarkMode}
                  />
                )}
              </div>
            </div> */}

            {/* Status Checklist */}
            <div
              className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <h3
                className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Status Checklist
              </h3>
              <div className="space-y-2">
                <ChecklistItem
                  label="Finance Approved"
                  checked={project.financeApproved}
                  onChange={() =>
                    handleChecklistToggle(
                      "financeApproved",
                      project.financeApproved,
                    )
                  }
                  isDarkMode={isDarkMode}
                />
                <ChecklistItem
                  label="Hardware Ordered"
                  checked={project.hardwareOrdered}
                  onChange={() =>
                    handleChecklistToggle(
                      "hardwareOrdered",
                      project.hardwareOrdered,
                    )
                  }
                  isDarkMode={isDarkMode}
                />
                <ChecklistItem
                  label="Kit List Created"
                  checked={project.kitListCreated}
                  onChange={() =>
                    handleChecklistToggle(
                      "kitListCreated",
                      project.kitListCreated,
                    )
                  }
                  isDarkMode={isDarkMode}
                />
                <ChecklistItem
                  label="Paperwork Checked"
                  checked={project.paperworkCheckedAndSaved}
                  onChange={() =>
                    handleChecklistToggle(
                      "paperworkCheckedAndSaved",
                      project.paperworkCheckedAndSaved,
                    )
                  }
                  isDarkMode={isDarkMode}
                />
                <ChecklistItem
                  label="Broadband Installed"
                  checked={!!project.broadbandInstalledDate}
                  disabled={true}
                  isDarkMode={isDarkMode}
                />
                <ChecklistItem
                  label="System Built"
                  checked={project.systemBuilt}
                  onChange={() =>
                    handleChecklistToggle("systemBuilt", project.systemBuilt)
                  }
                  isDarkMode={isDarkMode}
                />
                <ChecklistItem
                  label="Service Activity Complete"
                  checked={project.serviceActivityComplete}
                  onChange={() =>
                    handleChecklistToggle(
                      "serviceActivityComplete",
                      project.serviceActivityComplete,
                    )
                  }
                  isDarkMode={isDarkMode}
                />
                <ChecklistItem
                  label="Service Checker Complete"
                  checked={project.serviceCheckerComplete}
                  onChange={() =>
                    handleChecklistToggle(
                      "serviceCheckerComplete",
                      project.serviceCheckerComplete,
                    )
                  }
                  isDarkMode={isDarkMode}
                />
                <ChecklistItem
                  label="Tech Info Completed"
                  checked={project.techInfoCompleted}
                  onChange={() =>
                    handleChecklistToggle(
                      "techInfoCompleted",
                      project.techInfoCompleted,
                    )
                  }
                  isDarkMode={isDarkMode}
                />
              </div>
            </div>

            {/* Notes */}
            <div
              className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div
                className={`flex items-center justify-between mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                <h3 className="font-semibold">Notes ({notes.length})</h3>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Note title (optional)"
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg mb-2 focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <textarea
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                />
                <div className="flex items-center justify-between mt-2">
                  <label
                    className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <input
                      type="checkbox"
                      checked={isPrivateNote}
                      onChange={(e) => setIsPrivateNote(e.target.checked)}
                      className="rounded"
                    />
                    Private
                  </label>
                  <button
                    onClick={handleCreateNote}
                    disabled={!newNote.trim()}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Note
                  </button>
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {notes.slice(0, 3).map((note) => (
                  <div
                    key={note.id}
                    className={`p-3 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                  >
                    {note.title && (
                      <div
                        className={`font-medium text-sm mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {note.title}
                      </div>
                    )}
                    <p
                      className={`text-sm whitespace-pre-wrap line-clamp-3 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                    >
                      {note.body}
                    </p>
                    <div
                      className={`flex items-center justify-between mt-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      <span>{note.creator?.name}</span>
                      <span>
                        {new Date(note.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
                {notes.length === 0 && (
                  <p
                    className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No notes yet
                  </p>
                )}
              </div>
            </div>

            {/* Files */}
            <div
              className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3
                  className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Files ({files.length})
                </h3>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm">
                    <Plus size={16} />
                    Upload
                  </div>
                </label>
              </div>

              <div className="space-y-2">
                {files.slice(0, 5).map((file) => (
                  <div
                    key={file.id}
                    className={`flex items-center gap-3 p-2 rounded hover:bg-gray-800 cursor-pointer group ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                  >
                    <div className="text-xl">{getFileIcon(file.fileType)}</div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {file.title}
                      </div>
                      <div
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {formatFileSize(file.contentSize)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {file.fileUrl && (
                        <a
                          href={getFullFileUrl(file.fileUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`p-1 rounded ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={14} className="text-blue-400" />
                        </a>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFile(file.id);
                        }}
                        className={`p-1 rounded ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"}`}
                      >
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
                {files.length === 0 && (
                  <p
                    className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No files attached
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateBroadband && (
        <CreateBroadbandInfoModal
          account={project.account}
          project={project}
          onClose={() => setShowCreateBroadband(false)}
          onSuccess={() => {
            setShowCreateBroadband(false);
            fetchProject();
            fetchActivity();
          }}
        />
      )}

      {showCreatePorts && (
        <CreatePortsOrderModal
          project={project}
          onClose={() => setShowCreatePorts(false)}
          onSuccess={() => {
            setShowCreatePorts(false);
            fetchProject();
            fetchActivity();
          }}
        />
      )}

      {showCreateTask && (
        <CreateTaskModal
          project={project}
          onClose={() => setShowCreateTask(false)}
          onSuccess={() => {
            setShowCreateTask(false);
            fetchProject();
            fetchActivity();
          }}
        />
      )}

      {/* Drag and Drop Overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500/20 backdrop-blur-sm flex items-center justify-center z-50 pointer-events-none">
          <div
            className={`rounded-lg border-2 border-dashed border-blue-500 p-8 text-center ${isDarkMode ? "bg-[#13161B]" : "bg-white"}`}
          >
            <Paperclip size={48} className="mx-auto mb-4 text-blue-400" />
            <p
              className={`text-xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Drop files to upload
            </p>
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              Release to add files to this project
            </p>
          </div>
        </div>
      )}

      {/* Upload Confirmation Modal */}
      {showUploadConfirmation && filesToUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Paperclip className="text-blue-400" size={24} />
              </div>
              <div className="flex-1">
                <h3
                  className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Upload Files?
                </h3>
                <p
                  className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {filesToUpload.length === 1
                    ? `Upload "${filesToUpload[0].name}" to this project?`
                    : `Upload ${filesToUpload.length} files to this project?`}
                </p>

                {filesToUpload.length > 1 && (
                  <div className="space-y-1 mb-4 max-h-40 overflow-y-auto">
                    {Array.from(filesToUpload).map((file, idx) => (
                      <div
                        key={idx}
                        className={`text-sm flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        <Paperclip size={14} />
                        <span className="truncate">{file.name}</span>
                        <span className="text-xs">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={cancelUpload}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmUpload}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components
const DetailRow = ({ label, value, isDarkMode }) => (
  <div className="flex items-center justify-between text-sm">
    <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
      {label}
    </span>
    <span
      className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
    >
      {value}
    </span>
  </div>
);
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const getFileIcon = (fileType) => {
  if (fileType?.startsWith("image/")) return "🖼️";
  if (fileType?.startsWith("video/")) return "🎥";
  if (fileType?.startsWith("audio/")) return "🎵";
  if (fileType?.includes("pdf")) return "📄";
  if (fileType?.includes("word") || fileType?.includes("document")) return "📝";
  if (fileType?.includes("sheet") || fileType?.includes("excel")) return "📊";
  if (fileType?.includes("presentation") || fileType?.includes("powerpoint"))
    return "📽️";
  if (fileType?.includes("zip") || fileType?.includes("rar")) return "🗜️";
  return "📎";
};

const getFullFileUrl = (fileUrl) => {
  if (!fileUrl) return null;
  if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
    return fileUrl;
  }
  const S3_BASE_URL = "https://fibre-crm.s3.eu-west-2.amazonaws.com/";
  return S3_BASE_URL + fileUrl;
};
const ChecklistItem = ({
  label,
  checked,
  onChange,
  disabled = false,
  isDarkMode,
}) => (
  <div
    className={`flex items-center gap-2 ${
      !disabled ? "cursor-pointer hover:bg-gray-700/30 rounded p-1 -m-1" : ""
    }`}
    onClick={!disabled ? onChange : undefined}
  >
    <div
      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
        checked
          ? "bg-green-500 border-green-500"
          : isDarkMode
            ? "border-gray-600 bg-transparent"
            : "border-gray-300 bg-transparent"
      } ${!disabled ? "hover:border-green-400" : ""}`}
    >
      {checked && <CheckSquare className="text-white" size={12} />}
    </div>
    <span
      className={`text-sm ${checked ? (isDarkMode ? "text-white" : "text-gray-900") : isDarkMode ? "text-gray-400" : "text-gray-500"}`}
    >
      {label}
    </span>
  </div>
);

// Editable Field Component
const EditableField = ({
  label,
  field,
  value,
  type = "text",
  options = null,
  readOnly = false,
  isEditing,
  editingValue,
  savingField,
  onStartEdit,
  onCancel,
  onSave,
  onChange,
  // For lookup fields
  searchValue,
  onSearchChange,
  showSearch,
  setShowSearch,
  searchResults,
  loadingSearch,
  onSelectResult,
  renderSearchResult,
  isDarkMode,
}) => {
  if (readOnly) {
    return (
      <div>
        <label
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
        >
          {label}
        </label>
        <p className={isDarkMode ? "text-white" : "text-gray-900"}>
          {value || "—"}
        </p>
      </div>
    );
  }

  return (
    <div className="group">
      <label
        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"} block mb-1`}
      >
        {label}
      </label>
      <div className="flex items-center gap-2">
        {isEditing ? (
          <div className="flex-1 flex items-center gap-2">
            {options ? (
              <select
                value={editingValue}
                onChange={(e) => onChange(e.target.value)}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                autoFocus
              >
                <option value="">Select...</option>
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : type === "textarea" ? (
              <textarea
                value={editingValue}
                onChange={(e) => onChange(e.target.value)}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 resize-none ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                rows={3}
                autoFocus
              />
            ) : type === "date" ? (
              <input
                type="date"
                value={editingValue ? editingValue.split("T")[0] : ""}
                onChange={(e) => onChange(e.target.value)}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                autoFocus
              />
            ) : type === "lookup" ? (
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => {
                    onSearchChange(e.target.value);
                    setShowSearch(true);
                  }}
                  onFocus={() => setShowSearch(true)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                  placeholder={`Search ${label.toLowerCase()}...`}
                  autoFocus
                />
                {showSearch && searchValue && (
                  <div
                    className={`absolute top-full mt-1 w-full border rounded-lg shadow-xl max-h-48 overflow-y-auto z-20 ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
                  >
                    {loadingSearch ? (
                      <div className="p-4 text-center text-gray-400">
                        <Loader size={20} className="animate-spin mx-auto" />
                      </div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((result) => (
                        <button
                          key={result.id}
                          type="button"
                          onClick={() => onSelectResult(result)}
                          className={`w-full px-4 py-2 text-left transition-colors border-b last:border-b-0 ${isDarkMode ? "hover:bg-gray-700 border-gray-700" : "hover:bg-gray-100 border-gray-200"}`}
                        >
                          {renderSearchResult(result)}
                        </button>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-400">
                        No results found
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : type === "checkbox" ? (
              <input
                type="checkbox"
                checked={editingValue}
                onChange={(e) => onChange(e.target.checked)}
                className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-blue-600 focus:ring-blue-500"
              />
            ) : (
              <input
                type={type}
                value={editingValue}
                onChange={(e) => onChange(e.target.value)}
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                autoFocus
              />
            )}
            <button
              onClick={onSave}
              disabled={savingField}
              className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {savingField ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
            </button>
            <button
              onClick={onCancel}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
            >
              <CloseIcon size={16} />
            </button>
          </div>
        ) : (
          <>
            <p
              className={`flex-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {type === "date" && value
                ? new Date(value).toLocaleDateString()
                : type === "checkbox"
                  ? value
                    ? "Yes"
                    : "No"
                  : value || "—"}
            </p>
            <button
              onClick={onStartEdit}
              className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
            >
              <Edit
                size={14}
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
              />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Details Tab Component
const DetailsTab = ({
  project,
  editingField,
  editingValue,
  savingField,
  startEditing,
  cancelEditing,
  saveFieldEdit,
  setEditingValue,
  userSearch,
  setUserSearch,
  contactSearch,
  setContactSearch,
  showUserSearch,
  setShowUserSearch,
  showContactSearch,
  setShowContactSearch,
  users,
  contacts,
  loadingUsers,
  loadingContacts,
  selectUser,
  selectContact,
  filterUsers,
  isDarkMode,
}) => (
  <div className="space-y-6">
    {/* Description */}
    {project.description && (
      <div>
        <h3
          className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          <FileText size={18} />
          Description
        </h3>
        <div
          className={`whitespace-pre-wrap rounded-lg p-4 ${isDarkMode ? "text-gray-300 bg-gray-800/50" : "text-gray-600 bg-gray-100"}`}
        >
          {project.description}
        </div>
      </div>
    )}

    {/* Basic Information */}
    <div
      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
    >
      <h3
        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        <Briefcase size={18} />
        Basic Information
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <EditableField
          label="Project Name"
          field="projectName"
          value={project.projectName}
          isEditing={editingField === "projectName"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() => startEditing("projectName", project.projectName)}
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("projectName")}
          onChange={setEditingValue}
          isDarkMode={isDarkMode}
        />
        <EditableField
          label="Project Number"
          field="projectNumber"
          value={project.projectNumber}
          isEditing={editingField === "projectNumber"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() =>
            startEditing("projectNumber", project.projectNumber)
          }
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("projectNumber")}
          onChange={setEditingValue}
          isDarkMode={isDarkMode}
        />
        <EditableField
          label="Status"
          field="status"
          value={project.status}
          type="select"
          options={[
            { value: "Survey Requested", label: "Survey Requested" },
            { value: "Awaiting Survey", label: "Awaiting Survey" },
            { value: "Survey Booked", label: "Survey Booked" },
            { value: "Survey Completed", label: "Survey Completed" },
            { value: "Quotation", label: "Quotation" },
            { value: "Installation", label: "Installation" },
            {
              value: "Awaiting Finance Approval",
              label: "Awaiting Finance Approval",
            },
            { value: "Porting", label: "Porting" },
            { value: "Broadband Install", label: "Broadband Install" },
            { value: "Complete", label: "Complete" },
            { value: "Cancelled", label: "Cancelled" },
          ]}
          isEditing={editingField === "status"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() => startEditing("status", project.status)}
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("status")}
          onChange={setEditingValue}
          isDarkMode={isDarkMode}
        />
        <EditableField
          label="Priority"
          field="priority"
          value={project.priority}
          type="select"
          options={[
            { value: "Low", label: "Low" },
            { value: "Medium", label: "Medium" },
            { value: "High", label: "High" },
            { value: "Urgent", label: "Urgent" },
          ]}
          isEditing={editingField === "priority"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() => startEditing("priority", project.priority)}
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("priority")}
          onChange={setEditingValue}
          isDarkMode={isDarkMode}
        />
        <EditableField
          label="Type"
          field="type"
          value={project.type}
          isEditing={editingField === "type"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() => startEditing("type", project.type)}
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("type")}
          onChange={setEditingValue}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>

    {/* Project Team */}
    <div
      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
    >
      <h3
        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        <UserIcon size={18} />
        Project Team
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <EditableField
          label="Project Manager"
          field="projectManagerId"
          value={project.projectManager?.name}
          type="lookup"
          isEditing={editingField === "projectManagerId"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() =>
            startEditing("projectManagerId", project.projectManagerId)
          }
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("projectManagerId")}
          onChange={setEditingValue}
          searchValue={userSearch}
          onSearchChange={setUserSearch}
          showSearch={showUserSearch}
          setShowSearch={setShowUserSearch}
          searchResults={filterUsers(userSearch)}
          loadingSearch={loadingUsers}
          onSelectResult={selectUser}
          renderSearchResult={(user) => (
            <div>
              <div
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {user.name}
              </div>
              <div
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {user.email}
              </div>
            </div>
          )}
          isDarkMode={isDarkMode}
        />
        <EditableField
          label="Survey Completer"
          field="surveyCompletedBy"
          value={project.surveyCompleter?.name}
          type="lookup"
          isEditing={editingField === "surveyCompletedBy"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() =>
            startEditing("surveyCompletedBy", project.surveyCompletedBy)
          }
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("surveyCompletedBy")}
          onChange={setEditingValue}
          searchValue={userSearch}
          onSearchChange={setUserSearch}
          showSearch={showUserSearch}
          setShowSearch={setShowUserSearch}
          searchResults={filterUsers(userSearch)}
          loadingSearch={loadingUsers}
          onSelectResult={selectUser}
          renderSearchResult={(user) => (
            <div>
              <div
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {user.name}
              </div>
              <div
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {user.email}
              </div>
            </div>
          )}
          isDarkMode={isDarkMode}
        />
        <EditableField
          label="Survey Contact"
          field="clientSurveyContactId"
          value={
            project.clientSurveyContact
              ? `${project.clientSurveyContact.firstName} ${project.clientSurveyContact.lastName}`
              : null
          }
          type="lookup"
          isEditing={editingField === "clientSurveyContactId"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() =>
            startEditing("clientSurveyContactId", project.clientSurveyContactId)
          }
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("clientSurveyContactId")}
          onChange={setEditingValue}
          searchValue={contactSearch}
          onSearchChange={setContactSearch}
          showSearch={showContactSearch}
          setShowSearch={setShowContactSearch}
          searchResults={contacts}
          loadingSearch={loadingContacts}
          onSelectResult={selectContact}
          renderSearchResult={(contact) => (
            <div>
              <div
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {contact.firstName} {contact.lastName}
              </div>
              <div
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {contact.email}
              </div>
            </div>
          )}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>

    {/* Timeline */}
    <div
      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
    >
      <h3
        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        <Calendar size={18} />
        Timeline
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <EditableField
          label="Start Date"
          field="startDate"
          value={project.startDate}
          type="date"
          isEditing={editingField === "startDate"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() => startEditing("startDate", project.startDate)}
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("startDate")}
          onChange={setEditingValue}
          isDarkMode={isDarkMode}
        />
        <EditableField
          label="End Date"
          field="endDate"
          value={project.endDate}
          type="date"
          isEditing={editingField === "endDate"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() => startEditing("endDate", project.endDate)}
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("endDate")}
          onChange={setEditingValue}
          isDarkMode={isDarkMode}
        />
        <EditableField
          label="Installation Date"
          field="installationDate"
          value={project.installationDate}
          type="date"
          isEditing={editingField === "installationDate"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() =>
            startEditing("installationDate", project.installationDate)
          }
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("installationDate")}
          onChange={setEditingValue}
          isDarkMode={isDarkMode}
        />
        <EditableField
          label="Survey Completed Date"
          field="surveyCompletedDate"
          value={project.surveyCompletedDate}
          type="date"
          isEditing={editingField === "surveyCompletedDate"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() =>
            startEditing("surveyCompletedDate", project.surveyCompletedDate)
          }
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("surveyCompletedDate")}
          onChange={setEditingValue}
          isDarkMode={isDarkMode}
        />
        <EditableField
          label="Broadband Installed Date"
          field="broadbandInstalledDate"
          value={project.broadbandInstalledDate}
          type="date"
          isEditing={editingField === "broadbandInstalledDate"}
          editingValue={editingValue}
          savingField={savingField}
          onStartEdit={() =>
            startEditing(
              "broadbandInstalledDate",
              project.broadbandInstalledDate,
            )
          }
          onCancel={cancelEditing}
          onSave={() => saveFieldEdit("broadbandInstalledDate")}
          onChange={setEditingValue}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>

    {/* Blocking Status */}
    {project.projectBlocked && (
      <div
        className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      >
        <h3
          className={`text-lg font-semibold mb-4 flex items-center gap-2 text-red-400 ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          <AlertCircle size={18} />
          Blocking Information
        </h3>
        <div
          className={`border rounded-lg p-4 ${isDarkMode ? "bg-red-500/10 border-red-500/20" : "bg-red-50 border-red-200"}`}
        >
          <EditableField
            label="Blocked Reason"
            field="projectBlockedReason"
            value={project.projectBlockedReason}
            type="textarea"
            isEditing={editingField === "projectBlockedReason"}
            editingValue={editingValue}
            savingField={savingField}
            onStartEdit={() =>
              startEditing("projectBlockedReason", project.projectBlockedReason)
            }
            onCancel={cancelEditing}
            onSave={() => saveFieldEdit("projectBlockedReason")}
            onChange={setEditingValue}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>
    )}

    {/* Project Notes */}
    {project.projectNotes && (
      <div
        className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      >
        <h3
          className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          Project Notes
        </h3>
        <div
          className={`whitespace-pre-wrap rounded-lg p-4 ${isDarkMode ? "text-gray-300 bg-gray-800/50" : "text-gray-600 bg-gray-100"}`}
        >
          {project.projectNotes}
        </div>
      </div>
    )}

    {/* System Information */}
    <div
      className={`border-t pt-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
    >
      <h3
        className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        System Information
      </h3>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <label className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Created By
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {project.creator?.name || "System"}
          </p>
        </div>
        <div>
          <label className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Created Date
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {new Date(project.createdAt).toLocaleString()}
          </p>
        </div>
        <div>
          <label className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Last Modified By
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {project.lastModifiedBy?.name || "—"}
          </p>
        </div>
        <div>
          <label className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Last Modified Date
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {new Date(project.updatedAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Activity Tab Component
const ActivityTab = ({
  activity,
  formatActivityDate,
  getActivityIcon,
  isDarkMode,
}) => (
  <div className="space-y-6">
    {activity.length === 0 ? (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        <Activity size={48} className="mx-auto mb-2 opacity-50" />
        <p>No activity yet</p>
      </div>
    ) : (
      <div className="space-y-4">
        {activity.map((item, index) => (
          <div key={index} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}
              >
                {getActivityIcon(item.type)}
              </div>
              {index < activity.length - 1 && (
                <div
                  className={`w-0.5 flex-1 mt-2 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                />
              )}
            </div>
            <div className="flex-1 pb-4">
              <div className="flex items-start justify-between mb-1">
                <div
                  className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {item.title || item.subject}
                </div>
                <div
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {formatActivityDate(item.createdAt)}
                </div>
              </div>
              {item.description && (
                <div
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {item.description}
                </div>
              )}
              {item.creator && (
                <div className="flex items-center gap-2 mt-2">
                  <div
                    className={`w-6 h-6 rounded-full overflow-hidden ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
                  >
                    {item.creator.profileImageUrl ? (
                      <img
                        src={item.creator.profileImageUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs">
                        {item.creator.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <span
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {item.creator.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Tasks Tab Component
const TasksTab = ({ tasks, onCreateNew, navigate, isDarkMode }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between mb-4">
      <h3
        className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        Project Tasks ({tasks.length})
      </h3>
    </div>

    {tasks.length === 0 ? (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        <CheckSquare size={48} className="mx-auto mb-2 opacity-50" />
        <p className="mb-4">No tasks yet</p>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          Add First Task
        </button>
      </div>
    ) : (
      <div className="space-y-2">
        {tasks.map((task) => (
          <div
            key={task.id}
            onClick={() => navigate(`/project-tasks/${task.id}`)}
            className={`p-4 rounded-lg transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {task.projectTaskName}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      task.status === "Completed"
                        ? "bg-green-500/20 text-green-400"
                        : task.status === "In Progress"
                          ? "bg-blue-500/20 text-blue-400"
                          : task.status === "On Hold"
                            ? "bg-yellow-500/20 text-yellow-400"
                            : "bg-gray-500/20 text-gray-400"
                    }`}
                  >
                    {task.status}
                  </span>
                  {task.priority && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        task.priority === "Urgent"
                          ? "bg-red-500/20 text-red-400"
                          : task.priority === "High"
                            ? "bg-orange-500/20 text-orange-400"
                            : task.priority === "Medium"
                              ? "bg-yellow-500/20 text-yellow-400"
                              : "bg-blue-500/20 text-blue-400"
                      }`}
                    >
                      {task.priority}
                    </span>
                  )}
                </div>
                {task.subject && (
                  <div
                    className={`text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {task.subject}
                  </div>
                )}
              </div>
              <ExternalLink
                size={16}
                className={isDarkMode ? "text-gray-400" : "text-gray-400"}
              />
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Broadband Tab Component
const BroadbandTab = ({
  broadbandInfos,
  onCreateNew,
  navigate,
  isDarkMode,
}) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between mb-4">
      <h3
        className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        Broadband Information ({broadbandInfos.length})
      </h3>
      <button
        onClick={onCreateNew}
        className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
      >
        <Plus size={16} />
        New Broadband Info
      </button>
    </div>

    {broadbandInfos.length === 0 ? (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        <Wifi size={48} className="mx-auto mb-2 opacity-50" />
        <p className="mb-4">No broadband information yet</p>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          Add First Broadband Info
        </button>
      </div>
    ) : (
      <div className="space-y-2">
        {broadbandInfos.map((info) => (
          <div
            key={info.id}
            className={`p-4 rounded-lg transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
            onClick={() => navigate(`/broadband-information/${info.id}`)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {info.type || "Broadband"}
                  </span>
                  {info.broadbandInstalled && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                      Installed
                    </span>
                  )}
                </div>
                <div
                  className={`flex items-center gap-4 mt-2 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {info.broadbandSupplier && (
                    <span>Supplier: {info.broadbandSupplier}</span>
                  )}
                  {info.orderType && (
                    <>
                      <span>•</span>
                      <span>{info.orderType}</span>
                    </>
                  )}
                  {info.expectedSpeeds && (
                    <>
                      <span>•</span>
                      <span>{info.expectedSpeeds}</span>
                    </>
                  )}
                </div>
              </div>
              <ExternalLink
                size={16}
                className={isDarkMode ? "text-gray-400" : "text-gray-400"}
              />
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Ports Tab Component
const PortsTab = ({ portsOrders, onCreateNew, navigate, isDarkMode }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between mb-4">
      <h3
        className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        Ports Orders ({portsOrders.length})
      </h3>
      <button
        onClick={onCreateNew}
        className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm"
      >
        <Plus size={16} />
        New Ports Order
      </button>
    </div>

    {portsOrders.length === 0 ? (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        <PhoneCall size={48} className="mx-auto mb-2 opacity-50" />
        <p className="mb-4">No ports orders yet</p>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          Add First Ports Order
        </button>
      </div>
    ) : (
      <div className="space-y-2">
        {portsOrders.map((order) => (
          <div
            key={order.id}
            className={`p-4 rounded-lg transition-colors cursor-pointer ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
            onClick={() => navigate(`/ports-order/${order.id}`)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {order.portsOrderName || "Ports Order"}
                  </span>
                  {order.portCompleted && (
                    <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                      Completed
                    </span>
                  )}
                </div>
                {order.numbersToPort && (
                  <div
                    className={`text-sm mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Numbers: {order.numbersToPort}
                  </div>
                )}
                <div
                  className={`flex items-center gap-4 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {order.requestedDate && (
                    <span>
                      Requested:{" "}
                      {new Date(order.requestedDate).toLocaleDateString()}
                    </span>
                  )}
                  {order.acceptedDate && (
                    <>
                      <span>•</span>
                      <span>
                        Accepted:{" "}
                        {new Date(order.acceptedDate).toLocaleDateString()}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <ExternalLink
                size={16}
                className={isDarkMode ? "text-gray-400" : "text-gray-400"}
              />
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Notes Tab Component
const NotesTab = ({ notes, isDarkMode }) => (
  <div className="space-y-4">
    {notes.length === 0 ? (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        <FileText size={48} className="mx-auto mb-2 opacity-50" />
        <p>No notes yet</p>
      </div>
    ) : (
      <div className="space-y-3">
        {notes.map((note) => (
          <div
            key={note.id}
            className={`p-4 rounded-lg ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
          >
            {note.title && (
              <div
                className={`font-medium text-sm mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {note.title}
              </div>
            )}
            <p
              className={`text-sm whitespace-pre-wrap ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              {note.body}
            </p>
            <div
              className={`flex items-center justify-between mt-3 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              <span>{note.creator?.name}</span>
              <span>{new Date(note.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Files Tab Component
const FilesTab = ({ files, onUpload, onDelete, isDarkMode }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between mb-4">
      <h3
        className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        Files ({files.length})
      </h3>
      <label className="cursor-pointer">
        <input type="file" onChange={onUpload} className="hidden" />
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm">
          <Plus size={16} />
          Upload
        </div>
      </label>
    </div>

    {files.length === 0 ? (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        <Paperclip size={48} className="mx-auto mb-2 opacity-50" />
        <p className="mb-4">No files attached</p>
        <label className="cursor-pointer">
          <input type="file" onChange={onUpload} className="hidden" />
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
            <Plus size={16} />
            Upload First File
          </div>
        </label>
      </div>
    ) : (
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {files.map((file) => (
          <div
            key={file.id}
            className={`group flex items-center gap-3 p-3 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-100 hover:bg-gray-200"}`}
          >
            <div className="text-2xl">{getFileIcon(file.fileType)}</div>
            <div className="flex-1 min-w-0">
              <div
                className={`text-sm font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                title={file.title}
              >
                {file.title}
              </div>
              <div
                className={`text-xs flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                <span>{formatFileSize(file.contentSize)}</span>
                <span>•</span>
                <span>{new Date(file.createdAt).toLocaleDateString()}</span>
                {file.creator && (
                  <>
                    <span>•</span>
                    <span>{file.creator.name}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {file.fileUrl && (
                <a
                  href={getFullFileUrl(file.fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-1.5 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-300"}`}
                  title="Download"
                >
                  <ExternalLink size={16} className="text-blue-400" />
                </a>
              )}
              <button
                onClick={() => onDelete(file.id)}
                className={`p-1.5 rounded transition-colors ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-300"}`}
                title="Delete"
              >
                <Trash2 size={16} className="text-red-400" />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default ProjectDetail;
